import React, { createContext, useContext, useEffect } from 'react';

// The scroll source for a subtree. `null` means the document/window is the
// scrolling element — the default outside any TabContainer.
type ScrollSource = HTMLElement | null;

const ScrollContainerContext = createContext<ScrollSource>(null);

export const ScrollContainerProvider = ScrollContainerContext.Provider;

/**
 * Returns the scroll element for the current subtree, or `null` when the
 * window/document scrolls (default).
 *
 * Components inside a TabContainer get the tab's overflow:auto element.
 * Components outside (movie detail, watchlists, etc.) get `null` and should
 * fall back to `window`.
 */
export function useScrollContainer(): ScrollSource {
  return useContext(ScrollContainerContext);
}

/** Current scrollTop for the active source — works for both window and element. */
export function getScrollTop(source: ScrollSource): number {
  return source ? source.scrollTop : window.scrollY;
}

/** scrollTo for the active source. */
export function scrollSourceTo(source: ScrollSource, options: ScrollToOptions): void {
  if (source) source.scrollTo(options);
  else window.scrollTo(options);
}

/**
 * Attach a scroll listener to the active source. The handler re-binds if the
 * source changes (e.g. component mounts inside a new TabContainer).
 */
export function useScrollListener(
  handler: (event: Event) => void,
  options?: AddEventListenerOptions
): void {
  const source = useScrollContainer();
  useEffect(() => {
    const target: EventTarget = source ?? window;
    target.addEventListener('scroll', handler, options);
    return () => target.removeEventListener('scroll', handler, options);
    // handler/options intentionally not re-bound on every render — callers
    // should memoize the handler if needed. We re-bind only on source change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);
}
